import { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import moment from 'moment'
import { CvsDatePicker, CvsAutoComplete } from '@components'
import { DeleteButton } from './delete-button'
import {
  DATE_FILTER_FORMAT,
  DateFilterOption,
  FilterOperators,
  IntegrationAssignedFilter,
  IntegrationFilter,
  IntegrationFilterValue,
} from './filter-types'

const RowContainer = styled.div`
  position: relative;
  width: 100%;
  padding: 4;
  padding-left: 12;
  margin: 0;
`

const Spacer = styled.div`
  width: 45px;
`

const IntegrationDateFilter = styled.div`
  margin-top: 16px;

  .MuiInputBase-input {
    padding: 9px;
  }
`

/**
 * Currently, Meta date filters does not support all of our options (DATE_FILTER_OPTIONS).
 * We have to manually remove the unsupported filters in case of inbound Meta.
 */
const INBOUND_META_SLUG = 'facebook-marketing-inbound'

const META_SUPPORTED_START_DATE_FILTERS_KEY = [
  'campaign_start_date_filter',
  'ad_set_start_date_filter',
  'ads_start_date_filter',
]

const META_SUPPORTED_END_DATE_FILTERS_KEY = [
  'campaign_end_date_filter',
  'ad_set_end_date_filter',
  'ads_end_date_filter',
]

interface Props {
  filter: IntegrationAssignedFilter
  filterOption: IntegrationFilter
  onFilterChange: (value: IntegrationFilterValue[], filter: IntegrationAssignedFilter) => void
  onFilterDelete: (filter: IntegrationAssignedFilter) => void
  previousYearsRestriction?: number
  integrationSlug?: string
}

export const DateFilter = ({
  filter,
  filterOption,
  onFilterChange,
  onFilterDelete,
  previousYearsRestriction,
  integrationSlug,
}: Props) => {
  const selectedValue = filter.values.length ? filter.values.map(f => f.value)[0] : undefined

  const DATE_FILTER_OPTIONS = [
    {
      label: 'Before',
      value: '<',
    },
    {
      label: 'On or Before',
      value: '<=',
    },
    {
      label: 'After',
      value: '>',
    },
    {
      label: 'On or after',
      value: '>=',
    },
  ] as const satisfies DateFilterOption[]

  const date = selectedValue ? moment(selectedValue) : moment()
  const [isMetaInbound, setIsMetaInbound] = useState(false)
  const [metaFilter, setMetaFilter] = useState<DateFilterOption[]>([])

  useEffect(() => {
    /**
     * Here we check for inbound meta and only return the supported options
     * for start_date and end_date filters.
     */
    if (integrationSlug && integrationSlug === INBOUND_META_SLUG) {
      setIsMetaInbound(true)
      if (META_SUPPORTED_START_DATE_FILTERS_KEY.includes(filterOption.filterKey)) {
        setMetaFilter(
          DATE_FILTER_OPTIONS.filter(opt => {
            return opt.value === '>='
          }),
        )
      } else if (META_SUPPORTED_END_DATE_FILTERS_KEY.includes(filterOption.filterKey)) {
        setMetaFilter(
          DATE_FILTER_OPTIONS.filter(opt => {
            return opt.value === '<='
          }),
        )
      }
    }
  }, [])

  useEffect(() => {
    if (isMetaInbound && metaFilter[0]) {
      onFilterChange(filter.values, {
        ...filter,
        operator: metaFilter[0].value,
      })
    }
  }, [isMetaInbound, metaFilter, onFilterChange])

  function handleFilterChange(value): void {
    const newDate = value.format(DATE_FILTER_FORMAT)
    const filterValue: IntegrationFilterValue[] = [
      {
        integration_assignment_filters_id: filter.id,
        value: newDate,
        display_name: newDate,
      },
    ]
    onFilterChange(filterValue, filter)
  }

  function handleOperatorChange(value: FilterOperators): void {
    const updatedFilter: IntegrationAssignedFilter = {
      ...filter,
      operator: value,
    }
    onFilterChange(filter.values, updatedFilter)
  }

  function dateShouldBeDisabled(day) {
    if (!previousYearsRestriction) {
      return false
    }

    const yearsRestriction = moment()
      .subtract(previousYearsRestriction, 'years')
      .format('YYYY-MM-DD')

    const date = moment(day)
    if (date.isBefore(yearsRestriction)) {
      return true
    }
    return false
  }

  return (
    <RowContainer>
      <div
        style={{
          display: 'flex',
        }}
      >
        <CvsAutoComplete
          options={(isMetaInbound ? metaFilter : DATE_FILTER_OPTIONS) as never[]}
          style={{
            width: 450,
          }}
          margin="dense"
          label={filterOption.displayName}
          selected={
            isMetaInbound ? metaFilter[0]?.value : filter.operator || DATE_FILTER_OPTIONS[0].value
          }
          onSelected={handleOperatorChange}
          styleOverrides={{
            container: base => ({
              ...base,
              '& .MuiOutlinedInput-root': {
                height: 37,
              },
            }),
          }}
        />
        <Spacer />
        <IntegrationDateFilter>
          <CvsDatePicker
            style={{
              width: 450,
              marginTop: 15,
              flexGrow: 1,
            }}
            margin="dense"
            format="YYYY-MM-DD"
            handleChange={handleFilterChange}
            value={date as any}
            shouldDisableDate={dateShouldBeDisabled as any}
          />
        </IntegrationDateFilter>
        <DeleteButton
          filter={filter}
          onFilterDelete={onFilterDelete}
          marginTop={5}
          required={filterOption.required}
        />
      </div>
    </RowContainer>
  )
}
